import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { array, bool, func, number, object, string, oneOfType } from 'prop-types';
import { Link } from 'react-router-dom';
import { SLIDEOUTS } from '../../Slideout/Slideouts';
import classnames from 'classnames';
import InPlaceIndicator from 'ps-components/LoadingIndicator/InPlaceIndicator/InPlaceIndicator';
import isEqual from 'lodash/isEqual';
import isIE from 'ps-components/Utils/checkForIE';
import Placeholder from '../Placeholder/Placeholder';
import Popover from 'ps-components/Popover/Popover';
import Search from 'ps-components/Search/Search';

import './MarketplaceMatches.scss';
import MarketplaceMatchesGrid from './MarketplaceMatchesGrid/MarketplaceMatchesGrid';
import { getSelectedSurveys } from '../helper';

MarketplaceMatches.propTypes = {
  actions: object,
  addMatch: func,
  addSelectedCutID: func,
  benchmarkYear: number,
  currency: string,
  deleteMatch: func,
  dispatch: func,
  displayIn: string,
  error: string,
  getSurveyJobDetailsPageHref: func,
  grid: object,
  groupId: oneOfType([number, string]),
  handlePricingChange: func,
  hideSelect: bool,
  isAdHoc: bool,
  isGetJobPricingLoading: bool,
  isMatchDeleting: bool,
  isSlideout: bool,
  jobId: oneOfType([number, string]),
  jobPricingGrid: object,
  launchDarklyFeatureFlags: object,
  loadingId: oneOfType([number, string]),
  location: object,
  masterAccountId: string,
  numericRounding: number,
  onBack: func,
  onDataCutClick: func,
  onSurveyJobTitleClick: func,
  percentRounding: number,
  removeSelectedCutID: func,
  render: bool,
  renderPopover: bool,
  selectedRowId: number,
  selectedSurveys: array,
  selectedViewPk: number,
  settings: object,
  showMinHeader: bool,
  matches: object,
  toggleHeader: func,
  togglePopover: func,
  updateModifiedDate: func,
  viewLoading: bool
};

function MarketplaceMatches(props) {
  const [searchText, setSearchText] = useState('');
  const [showPurchasedSurveys, setShowPurchasedSurveys] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedSurveys, setSelectedSurveys] = useState([]);
  const [totalFilteredPurchasedCount, setTotalFilteredPurchasedCount] = useState();
  const [totalFilteredUnpurchasedCount, setTotalFilteredUnpurchasedCount] = useState();
  const [showAboutMatches, setShowAboutMatches] = useState(false);

  const changeSearchValue = useRef();
  const aboutMatchesRef = useRef();

  useEffect(() => {
    if (!props.render && !isEqual(props.selectedSurveys, selectedSurveys)) {
      setSelectedSurveys(props.selectedSurveys);
      setLoaded(false);
    }
    if (
      props.render &&
      !props.matches?.loading &&
      !props.matches?.purchasedLoaded &&
      !props.matches?.unpurchasedLoaded &&
      !loaded &&
      !loading
    ) {
      setLoading(true);
      const surveyJobs = getSelectedSurveys(props.jobPricingGrid);
      props.actions.getMarketplaceMatches(surveyJobs, props.currency, showPurchasedSurveys, true);
      setLoaded(true);
      setLoading(false);
    }
  }, [props.selectedSurveys]);

  useEffect(() => {
    if (props.render && !props.matches?.loading && !loading && loaded) {
      if (
        (showPurchasedSurveys && totalFilteredPurchasedCount == null) ||
        (!showPurchasedSurveys && totalFilteredUnpurchasedCount == null)
      ) {
        setLoading(true);
        const surveyJobs = getSelectedSurveys(props.jobPricingGrid);
        props.actions.getMarketplaceMatches(surveyJobs, props.currency, showPurchasedSurveys, false);
        setLoaded(true);
        setLoading(false);
      }
    }
  }, [showPurchasedSurveys]);

  useEffect(() => {
    if (props.render && !loaded && !loading) {
      props.actions.resetMarketplaceMatches();
      props.actions.getPreferences();
      props.actions.getGeographies();
      props.actions.getIndustries();
      setLoaded(false);
      props.handlePricingChange();
    }
  }, [props.render]);

  if (!props.render) {
    return <React.Fragment />;
  }

  // scrollRef used to scroll up to top after search
  let scrollRef = React.createRef();
  const counts = {
    purchased: props.matches?.purchased?.length,
    unpurchased: props.matches?.unpurchased?.length
  };

  function scrollAndSearch(searchText) {
    if (scrollRef?.current?.scrollTop) {
      scrollRef.current.scrollTop = 0; // reset scroll
    }
    setSearchText(searchText);
  }

  // clicking x to clear search text
  function searchOnClick() {
    scrollAndSearch('');
  }

  // when pressing enter to execute a search
  function searchOnKeyDown(event) {
    if (event.key === 'Enter') {
      scrollAndSearch(event.target.value);
    }
  }

  // clicking the search icon to execute a search
  function searchOnSearch(value) {
    scrollAndSearch(value);
  }

  // Determine whether data is loading
  const dataLoading = props.matches?.loading || props.viewLoading;

  const showNoMatchesPlaceholder =
    props.matches?.purchased == null &&
    props.matches?.unpurchased == null &&
    props.matches?.purchasedLoaded &&
    props.matches?.unpurchasedLoaded;

  const noMatchesPlaceholder = (
    <div className="marketplace-matches__no-results-container">
      <img className="no-results-img" src="/view/static/mp/images/market_place_matches.svg" />
      <Placeholder
        primaryText="Find Suggested Matches with Marketplace Matches"
        secondaryText="Suggestions are based on how other companies have market priced using the same survey jobs"
        showText={true}
      />
      <div className="marketplace-matches__why">
        <div className="marketplace-matches__why-header">Why do I not see marketplace matches for this job?</div>
        <span className="marketplace-matches__why-body">
          Either no survey job matches have been made or the current matches do not have any corresponding suggestions.
          Start adding matches or try different matches to see suggestions.
        </span>
      </div>
    </div>
  );

  // Checks for no matches/recommendations
  const showNothing =
    props.matches?.purchased == null &&
    props.matches?.unpurchased == null &&
    !props.matches?.purchasedLoaded &&
    !props.matches?.unpurchasedLoaded &&
    !props.matches?.loading;

  function showSettings() {
    props.actions.showSlideout(SLIDEOUTS.MARKETPLACE_MATCHES_SETTINGS, {
      onBack: props.onBack,
      showSlideout: props.actions.showSlideout,
      isBigger: props.isSlideout
    });
  }

  function togglePurchased(value) {
    setShowPurchasedSurveys(value);
    if (scrollRef?.current?.scrollTop) {
      scrollRef.current.scrollTop = 0; // reset scroll
    }
  }

  function toggleAboutMatches(e) {
    e?.stopPropagation(); 
    setShowAboutMatches(!showAboutMatches);
  }

  function getAboutMatchesText() {
    return (
      <React.Fragment>
        <div className="marketplace-matches__about-matches-title">{'What are marketplace matches?'}</div>
        <div className="marketplace-matches__about-matches-text">
          {`Marketplace matches are additional survey jobs based on your current survey job matches for this job. \
            These matches come from companies who selected the same matches for the same or similar job title. \
            Edit preferences to filter results.`}
        </div>
        <div className="marketplace-matches__about-matches-title">{'What are frequently matched matches?'}</div>
        <div className="marketplace-matches__about-matches-text">
          {`Other companies with similar matches have frequently matched these survey jobs, \
            indicating a high match likelihood.`}
        </div>
        <div className="marketplace-matches__about-matches-title">{'What are suggested surveys?'}</div>
        <div className="marketplace-matches__about-matches-text">
          {`Suggested surveys are surveys not currently in your survey library in which there may be \
            relevant matches for the job. Click on the Suggested Surveys tab to display potential matches \
            in other surveys.`}
        </div>
      </React.Fragment>
    );
  }

  function onScroll(e) {
    if (props.toggleHeader) {
      props.toggleHeader(e.target);
    }
  }

  return (
    <div className="marketplace-matches">
      <div className="marketplace-matches__content">
        {dataLoading || showNothing ? (
          <InPlaceIndicator />
        ) : (
          <React.Fragment>
            <div className="marketplace-matches__search-container">
              <Search
                className="marketplace-matches__search"
                onClick={searchOnClick}
                onKeyDown={searchOnKeyDown}
                onSearch={searchOnSearch}
                placeholder="Search Marketplace Matches"
                testId="marketplace-matches__search"
                useInternalState
                ref={changeSearchValue}
              />

              <div className={classnames('marketplace-matches__right-buttons', { ie: isIE() })}>
                <div className="marketplace-matches__links" ref={aboutMatchesRef}>
                  <Link
                    to="#"
                    id="about-matches-link"
                    className="marketplace-matches__link mpm-about-matches-link"
                    onClick={toggleAboutMatches}
                  >
                    About Matches
                  </Link>
                  <div id="marketplace-matches__about-popover" className="marketplace-matches__about-popover"></div>
                  <div className="marketplace-matches__seperator" />
                  <Link to="#" className="marketplace-matches__link mpm-edit-link" onClick={showSettings}>
                    Edit Filters
                  </Link>
                </div>

                <React.Fragment>
                  {showAboutMatches &&
                    ReactDOM.createPortal(
                      <Popover
                        triggerElement={aboutMatchesRef}
                        relativeElement={document.querySelector('about-matches-link')}
                        display={showAboutMatches}
                        togglePopover={toggleAboutMatches}
                      >
                        {getAboutMatchesText()}
                      </Popover>,
                      document.getElementById('marketplace-matches__about-popover')
                    )}
                </React.Fragment>

                <div
                  className={classnames('marketplace-matches__toggle', 'left', { active: showPurchasedSurveys })}
                  onClick={() => togglePurchased(true)}
                >
                  {totalFilteredPurchasedCount == null
                    ? 'Active Surveys'
                    : `Active Surveys (${totalFilteredPurchasedCount})`}
                </div>
                <div
                  className={classnames('marketplace-matches__toggle', 'right', { active: !showPurchasedSurveys })}
                  onClick={() => togglePurchased(false)}
                >
                  {totalFilteredUnpurchasedCount == null
                    ? 'Suggested Surveys'
                    : `Suggested Surveys (${totalFilteredUnpurchasedCount})`}
                </div>
              </div>
            </div>

            {showNoMatchesPlaceholder ? (
              noMatchesPlaceholder
            ) : (
              <React.Fragment>
                <div
                  ref={scrollRef}
                  onScroll={onScroll}
                  className={classnames('marketplace-matches__list-container', {
                    adhoc: props.isAdHoc,
                    min: props.showMinHeader,
                    unpurchased: !showPurchasedSurveys
                  })}
                >
                  <MarketplaceMatchesGrid
                    counts={counts}
                    filterText={searchText}
                    groupId={props.groupId}
                    showPurchasedSurveys={showPurchasedSurveys}
                    matches={props.matches}
                    masterAccountId={props.masterAccountId}
                    displayIn={props.displayIn}
                    benchmarkYear={props.benchmarkYear}
                    selectedViewPk={props.selectedViewPk}
                    currency={props.currency}
                    grid={props.grid}
                    actions={props.actions}
                    addMatch={props.addMatch}
                    settings={props.settings}
                    jobId={props.jobId}
                    loadingId={props.loadingId}
                    deleteMatch={props.deleteMatch}
                    updateModifiedDate={props.updateModifiedDate}
                    isMatchDeleting={props.isMatchDeleting}
                    togglePopover={props.togglePopover}
                    renderPopover={props.renderPopover}
                    selectedRowId={props.selectedRowId}
                    hideSelect={props.hideSelect}
                    onDataCutClick={props.onDataCutClick}
                    setTotalFilteredPurchasedCount={setTotalFilteredPurchasedCount}
                    setTotalFilteredUnpurchasedCount={setTotalFilteredUnpurchasedCount}
                    addSelectedCutID={props.addSelectedCutID}
                    removeSelectedCutID={props.removeSelectedCutID}
                    selectedSurveys={props.selectedSurveys}
                    onSurveyJobTitleClick={props.onSurveyJobTitleClick}
                    getSurveyJobDetailsPageHref={props.getSurveyJobDetailsPageHref}
                    onBack={props.onBack}
                    isSlideout={props.isSlideout}
                    numericRounding={props.numericRounding}
                    percentRounding={props.percentRounding}
                  />
                </div>
              </React.Fragment>
            )}
          </React.Fragment>
        )}
      </div>
    </div>
  );
}

export default MarketplaceMatches;
